import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import ModalPortal from "./ModalPortal";

/**
 * Truncated cell text with a "Show more" affordance that opens a
 * self-sizing dialog with the full content (ZY-10340).
 *
 * Consumed in three places in the Lab Investigations section:
 *   • main landing table row (`Description` column)
 *   • trend / audit table row (`Description` column)
 *   • correction-history dialog (Original / Latest description fields)
 *
 * Behaviour:
 *   • Renders as a single-line clamped span. On mount + on the text
 *     changing, we compare the rendered element's `scrollWidth` against
 *     its `clientWidth`; if it overflows, we show the "Show more" pill
 *     inline (kept simple: no ellipsis-aware click target, the pill
 *     itself is the click target).
 *   • Empty/whitespace text renders the em-dash placeholder verbatim
 *     with NO "Show more" — matching the existing "—" convention in
 *     the section tables.
 *   • Dialog is portalled to <body> (see ModalPortal for the sticky-
 *     column stacking-context rationale). Width and height are bounded
 *     by CSS max-width/max-height on `.li-td-dialog`; text wraps within
 *     the width, and long content scrolls vertically inside the
 *     `.li-td-dialog-body`. No horizontal scroll — wrap-down only.
 *   • Escape and backdrop click close the dialog.
 *   • Reduced-motion honoured by SCSS (no transitions declared here).
 *
 * `title` defaults to "Description" — override for other contexts.
 */
const TruncatedText = ({ text, title, emptyPlaceholder, className }) => {
  const spanRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [open, setOpen] = useState(false);

  const value = typeof text === "string" ? text : (text == null ? "" : String(text));
  const isEmpty = value.trim() === "";

  // Measure overflow after layout. Depends on the actual rendered text,
  // so re-runs when text changes. Ignored when empty — no chip needed.
  useLayoutEffect(() => {
    if (isEmpty) {
      setOverflowing(false);
      return;
    }
    const el = spanRef.current;
    if (!el) return;
    // A one-frame delay via the browser's own layout pass is enough
    // here — no ResizeObserver: the column widths that host this
    // component are grid-driven and don't animate. If a consumer ever
    // resizes its container dynamically, this will need a listener.
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, [value, isEmpty]);

  // Escape closes the dialog. Handler is only wired while open so we
  // don't leak listeners across mount cycles.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (isEmpty) {
    return <span className={`li-td-cell li-td-empty ${className || ""}`}>{emptyPlaceholder}</span>;
  }

  return (
    <>
      <span className={`li-td-cell ${className || ""}`}>
        <span ref={spanRef} className="li-td-clip">{value}</span>
        {overflowing && (
          <button
            type="button"
            className="li-td-more"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          >
            Show more
          </button>
        )}
      </span>
      {open && (
        <ModalPortal>
          <div
            className="li-td-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={() => setOpen(false)}
          >
            <div className="li-td-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="li-td-dialog-head">
                <span className="li-td-dialog-title">{title}</span>
                <button
                  type="button"
                  className="li-td-dialog-close"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="li-td-dialog-body">{value}</div>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
};

TruncatedText.propTypes = {
  text: PropTypes.string,
  /** Dialog title. Defaults to "Description". */
  title: PropTypes.string,
  /** Rendered when text is empty/whitespace. Defaults to em-dash. */
  emptyPlaceholder: PropTypes.node,
  /** Additional class on the inline span (e.g. cell-specific class). */
  className: PropTypes.string,
};

TruncatedText.defaultProps = {
  text: "",
  title: "Description",
  emptyPlaceholder: "—",
  className: "",
};

export default TruncatedText;
